import * as React from 'react';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Drawer
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AssessmentOutlined,
  PendingActionsOutlined,
  SummarizeOutlined,
  InsightsOutlined,
  Menu as MenuIcon
} from '@mui/icons-material';

import {
  fetchSurveyAnalytics,
  fetchSurveyResponses,
  fetchPendingResponses,
  setCurrentSurvey,
  fetchSurveyDetails,
  setDateRange,
  resetErrors
} from '../../store/surveyResponsesSlice';

import Results from '../../components/surveyResponses/Results';
import Progress from '../../components/surveyResponses/Progress';
import Summary from '../../components/surveyResponses/Summary';
import Analytics from '../../components/surveyResponses/Analytics';
import './SurveyResponses.css';

// TabPanel component remains unchanged
const TabPanel = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      className="tab-panel"
    >
      {value === index && children}
    </div>
  );
};

const SurveyResponses = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [activeTab, setActiveTab] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Select data from Redux store
  const { 
    analytics, 
    loading, 
    error, 
    stats, 
    dateRange,
    currentSurveyId,
    responses,
    surveyName
  } = useSelector(state => state.surveyResponses);

  // Survey name should now be available from surveyName field
  
  const isLoading = loading.analytics || loading.responses || loading.pendingResponses;
  const hasError = error.analytics || error.responses || error.pendingResponses;

  // useEffect(() => {
  //   if (surveyId) {
  //     dispatch(setCurrentSurvey(surveyId));
      
  //     // Use Promise.all to ensure all data is fetched 
  //     Promise.all([
  //       dispatch(fetchSurveyResponses(surveyId)).unwrap(),
  //       dispatch(fetchPendingResponses(surveyId)).unwrap()
  //     ]).then(() => {
  //       // Once we have all response data, fetch analytics with most current data
  //       dispatch(fetchSurveyAnalytics({surveyId, dateRange}));
  //     }).catch(error => {
  //       console.error('Error fetching data:', error);
  //     });
  //   }
  // }, [dispatch, surveyId, dateRange]);

  // Handle filter change
  
  
  useEffect(() => {
    if (surveyId) {
      dispatch(setCurrentSurvey(surveyId));
      
      // Fetch survey details first
      dispatch(fetchSurveyDetails(surveyId))
        .then(() => {
          // After getting survey details, fetch other data
          return Promise.all([
            dispatch(fetchSurveyResponses(surveyId)).unwrap(),
            dispatch(fetchPendingResponses(surveyId)).unwrap()
          ]);
        })
        .then(() => {
          // Once we have all response data, fetch analytics
          dispatch(fetchSurveyAnalytics({surveyId, dateRange}));
        })
        .catch(error => {
          console.error('Error fetching data:', error);
        });
    }
  }, [dispatch, surveyId, dateRange]);
  
  const handleDateRangeChange = (newDateRange) => {
    dispatch(setDateRange(newDateRange));
    dispatch(fetchSurveyAnalytics({surveyId, dateRange: newDateRange}));
  };

  // Handle retry
  const handleRetry = () => {
    dispatch(resetErrors());
    dispatch(fetchSurveyAnalytics({surveyId, dateRange}));
    dispatch(fetchSurveyResponses(surveyId));
    dispatch(fetchPendingResponses(surveyId));
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const TabsContent = () => (
    <Tabs
      orientation="vertical"
      value={activeTab}
      onChange={(e, newValue) => {
        setActiveTab(newValue);
        setMobileOpen(false);
      }}
      className="vertical-tabs"
      sx={{
        borderRight: 1,
        borderColor: 'divider',
        height: '100%',
        '& .MuiTab-root': {
          minHeight: 64,
          justifyContent: 'flex-start',
        }
      }}
    >
      <Tab
        icon={<AssessmentOutlined sx={{ fontSize: 24 }} />}
        label="Results"
        sx={{
          flexDirection: 'row',
          gap: 1.5,
          alignItems: 'center',
          fontSize: '0.95rem',
          fontWeight: 500,
        }}
      />
      <Tab
        icon={<PendingActionsOutlined sx={{ fontSize: 24 }} />}
        label="In Progress"
        sx={{
          flexDirection: 'row',
          gap: 1.5,
          alignItems: 'center',
          fontSize: '0.95rem',
          fontWeight: 500,
        }}
      />
      <Tab
        icon={<SummarizeOutlined sx={{ fontSize: 24 }} />}
        label="Summary"
        sx={{
          flexDirection: 'row',
          gap: 1.5,
          alignItems: 'center',
          fontSize: '0.95rem',
          fontWeight: 500,
        }}
      />
      <Tab
        icon={<InsightsOutlined sx={{ fontSize: 24 }} />}
        label="Analytics"
        sx={{
          flexDirection: 'row',
          gap: 1.5,
          alignItems: 'center',
          fontSize: '0.95rem',
          fontWeight: 500,
        }}
      />
    </Tabs>
  );

  if (isLoading && !analytics) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (hasError && !analytics) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert 
          severity="error" 
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={handleRetry}
            >
              Retry
            </IconButton>
          }
        >
          {error.analytics || error.responses || error.pendingResponses || 'An error occurred'}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box 
        sx={{ 
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 1100
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ py: 2, display: 'flex', alignItems: 'center' }}>
            <IconButton 
              edge="start" 
              onClick={() => navigate(-1)}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
              {surveyName || analytics?.survey_name || `Survey ${surveyId}` || 'Survey Responses'}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Box sx={{ display: 'flex' }}>
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: 240,
              bgcolor: 'background.paper'
            },
          }}
        >
          <Box sx={{ mt: 8 }}>
            <TabsContent />
          </Box>
        </Drawer>

        {/* Desktop Tabs */}
        <Box
          sx={{
            width: 240,
            flexShrink: 0,
            display: { xs: 'none', sm: 'block' },
            borderRight: 1,
            borderColor: 'divider'
          }}
        >
          <TabsContent />
        </Box>

        {/* Content Area */}
        <Box sx={{ 
          flexGrow: 1, 
          width: { xs: '100%', sm: `calc(100% - 240px)` },
          p: { xs: 2, sm: 3 }
        }}>
          <TabPanel value={activeTab} index={0}>
            <Results />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Progress />
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <Summary />
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <Analytics />
          </TabPanel>
        </Box>
      </Box>
    </Box>
  );
};

export default SurveyResponses;